import { StrictMode, FC, ReactElement } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import {
  BrowserRouter,
  Routes,
  Route,
  StaticRouter,
} from 'react-router-dom-v5-compat'
import { Hydrate, QueryClientProvider, dehydrate } from '@tanstack/react-query'
import { FlagsProvider } from 'flagged'
import { queryClient } from './queryClient'
import { ScrollToTop } from './ScrollToTop'
import { UIProvider } from './contexts/UIContext'

const isServer = typeof window === 'undefined'
const helmetContext = {}

const generateDynamicPath = (path: string) =>
  path
    .replace('src/routes', '')
    .replace('./', '/')
    .replace(/\(.*?\)/g, '') // Ignore folders name within parentheses
    .replace('.tsx', '')
    .replace(/\[(.*?)\]/g, ':$1') // Convert brackets into route parameters
    .replace(/\/+/g, '/')
    .replace('/Route', '')
    .replace('/Layout', '') || '/' // Replace empty string with base slash

const generateRoutes = (
  context: __WebpackModuleApi.RequireContext
): RouteConfig[] =>
  context.keys().map((key: string) => {
    const routePath = generateDynamicPath(key)
    const Component = context(key).default
    return { path: routePath, element: <Component /> }
  })

interface RouteConfig {
  path: string
  element: ReactElement
}

const removeDuplicates = (routes: RouteConfig[]): RouteConfig[] =>
  routes.reduce((acc: RouteConfig[], route) => {
    if (acc.some((r) => r.path === route.path)) {
      return acc
    }
    return [...acc, route]
  }, [])

const routeComponents = require.context('./routes', true, /Route.tsx$/)
const layoutComponents = require.context('./routes', true, /\/Layout.tsx$/)
// Require is doubling the routes, pulling from ./routes and src/routes
let routesConfig = generateRoutes(routeComponents)
routesConfig = removeDuplicates(routesConfig)
let layoutsConfig = generateRoutes(layoutComponents)
layoutsConfig = removeDuplicates(layoutsConfig)

// Sort layoutConfig by entry.path where entries with the most path parts come first
layoutsConfig.sort(
  (a, b) => b.path.split('/').length - a.path.split('/').length
)

// Group routesConfig entries by layout path
const assignedRoutes = new Set()
const groupedRoutes = layoutsConfig.map((layout) => {
  const layoutPath = layout.path
  const routes = routesConfig.filter((route) => {
    if (route.path.startsWith(layoutPath) && !assignedRoutes.has(route.path)) {
      assignedRoutes.add(route.path)
      return true
    }
    return false
  })
  return { layout, routes }
})
const ungroupedRoutes = routesConfig.filter(
  (route) => !assignedRoutes.has(route.path)
)

interface AppProps {
  hydrationData?: Array<{ key: Array<string | number>; data: any }>
  isMobileSSR?: boolean
  location?: string
  appWebView?: boolean
  featureFlags?: Record<string, boolean>
}

const App: FC<AppProps> = ({
  hydrationData,
  isMobileSSR = false,
  location,
  appWebView = false,
  featureFlags = {},
}) => {
  hydrationData?.forEach(({ key, data }) => {
    queryClient.setQueryData(key, data)
  })

  const renderRoutes = (): ReactElement => (
    <Routes>
      {groupedRoutes.map(({ layout, routes }) => (
        <Route element={layout.element} key={layout.path}>
          {routes.map((route) => (
            <Route element={route.element} key={route.path} path={route.path} />
          ))}
        </Route>
      ))}
      {ungroupedRoutes.map((route) => (
        <Route element={route.element} key={route.path} path={route.path} />
      ))}
      <Route element={<></>} path="*" />
    </Routes>
  )

  return (
    <StrictMode>
      <FlagsProvider features={featureFlags}>
        <QueryClientProvider client={queryClient}>
          <Hydrate state={dehydrate(queryClient)}>
            <HelmetProvider context={helmetContext}>
              <UIProvider appWebView={appWebView} isMobileSSR={isMobileSSR}>
                {isServer ? (
                  <StaticRouter location={location || '/'}>
                    {renderRoutes()}
                  </StaticRouter>
                ) : (
                  <BrowserRouter>
                    <ScrollToTop />
                    {renderRoutes()}
                  </BrowserRouter>
                )}
              </UIProvider>
            </HelmetProvider>
          </Hydrate>
        </QueryClientProvider>
      </FlagsProvider>
    </StrictMode>
  )
}

export default App
